import React, { useEffect, useState, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { fetchProductShops } from '@fh-components/fh-js-api-core/store/slices/productSlice';
import { getCurrency } from '@fh-components/fh-js-api-core/store/slices/currencySlice';
import {
	updateHistoryIds,
	getViewedData,
	fetchHistory,
	updateViewed,
} from '@fh-components/fh-js-api-core/store/slices/viewedSlice';
import { productDataSelector, productShopsSelector } from '@/store';
import { useSelector, useDispatch } from 'react-redux';
import { Container } from '@/components/styles';
import { useRouter } from 'next/router';
import { Loader } from '@/components';
// import useWindowSize from '@/hooks/use-window-size';
import { JsonLdData } from '@/seo/JsonLd';
import { MetaData, BreadCrumbs } from '@/containers';
import getProductWithoutQuantity from '@/lib/getProductWithoutQuantity';
import { getEmoji } from '@/lib/config';
import ecommerce from '@/seo/Ecommerce';
import useLocalStorage from '@/hooks/use-local-storage';
import { getObjectFromArray } from '@/utils';
import mindbox from '@/seo/Mindbox';
import qs from 'qs';
import Collections from './components/Collections';

import styles from './ProductPage.module.scss';

const Product = dynamic(() => import('../../containers/product'), {
	loading: () => <Loader />,
});

const Viewed = dynamic(() => import('../../containers/viewed'), {
	loading: () => <Loader />,
});

const isEqualIds = (ids, historyIds) => {
	return ids.every(id => historyIds[id]);
};

const ProductPage = () => {
	const router = useRouter();
	const data = useSelector(productDataSelector);
	const currency = useSelector(getCurrency);
	const [historyProducts, setHistoryProducts] = useState([]);
	const [firstProductId, setFirstProductId] = useState(-1);
	const [productInfoGA, setProductInfoGA] = useState({
		pageName: 'Страница товара',
		indexCard: 1,
	});
	const { query } = router;
	const { data: shopsData, loading: loadingShops } = useSelector(productShopsSelector);
	const { brand, name, price, seo, model, breadcrumbs_second = null } = data || {};
	const modelId = model?.id;
	const modelSlug = model?.slug;
	const { variant = null } = qs.parse(query);
	const title = `${name} ${brand} | Коллекция ${brand} на fh.by`;
	const description = `${name} ${brand} за ${price} byn в интернет-магазине FH.BY. ${getEmoji(
		'checkMark',
	)} Только оригинальные бренды. Удобный онлайн-каталог: цены, фото, описание. ${getEmoji(
		'deliveryTruck',
	)} Доставка по всей Беларуси`;

	const ogTitle = data?.og?.title ?? '';
	const ogDescription = data?.og?.description ?? '';
	const ogImageRect = data?.og?.image_big ?? null;
	const ogImageSquare = data?.og?.image_small ?? null;
	const [history, setHistory] = useLocalStorage('history', { count: 0, ids: {}, products: [] });
	const dispatch = useDispatch();
	const { count, ids, isFromSession } = useSelector(getViewedData);

	useEffect(() => {
		if (Object.keys(currency).length && typeof window !== 'undefined') {
			let productInfo = null;
			const productInfoStr = localStorage.getItem('productInfo');

			if (productInfoStr) {
				productInfo = JSON.parse(productInfoStr);
				setProductInfoGA(productInfo);
			}

			const newProducts = getProductWithoutQuantity([data]);

			ecommerce.viewItemList(
				newProducts,
				currency,
				productInfo?.pageName ?? 'Страница товара',
				'view_item',
				productInfo?.indexCard ?? 1,
			);
			window.localStorage.removeItem('productInfo');
		}
	}, [currency, modelId]);

	useEffect(() => {
		if (isFromSession && (history.count !== count || !isEqualIds(ids, history.ids))) {
			dispatch(
				fetchHistory({
					onSuccess: items => {
						const newIds = items.map(item => item.model.id);
						const idsObj = getObjectFromArray(newIds);

						dispatch(updateHistoryIds({ ids: newIds, count: newIds.length }));
						setHistory({
							count: newIds.length,
							ids: idsObj,
							products: items,
						});
					},
				}),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isFromSession, currency]);

	useEffect(() => {
		dispatch(
			updateViewed({
				model_id: modelId,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modelId, dispatch]);

	useEffect(() => {
		if (isFromSession && history.count === count && isEqualIds(ids, history.ids)) {
			if (!history.ids[modelId]) {
				dispatch(
					updateHistoryIds({
						ids: [modelId, ...ids],
						count: count + 1,
					}),
				);
				setHistory({
					count: count + 1,
					ids: {
						[modelId]: true,
						...history.ids,
					},
					products: [data, ...history.products],
				});
			} else {
				let indexToFirst = -1;

				if (history.products[0].model.id !== modelId) {
					const newArray = history.products.filter((item, index) => {
						if (item.model.id !== modelId) {
							return true;
						} else {
							indexToFirst = index;
							return false;
						}
					});
					if (indexToFirst > 0) {
						setHistory({
							...history,
							products: [history.products[indexToFirst], ...newArray],
						});
					}
				} else {
					setHistoryProducts(history.products.slice(1));
				}
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modelId, history, count, ids, data, isFromSession]);

	useEffect(() => {
		let uuid = data.uuid;
		let options = [];

		data?.attributes?.forEach(item => {
			if (item.name === 'Размер производителя') {
				options = [...item.options];
			}
		});

		if (options.length) {
			if (variant) {
				for (let i = 0; i < options.length; i++) {
					if (options[i].variant_id == variant) {
						uuid = options[i].uuid;

						break;
					}
				}
			} else {
				uuid = options[0].uuid;

				for (let i = 0; i < options.length; i++) {
					if (options[i].has) {
						uuid = options[i].uuid;

						break;
					}
				}
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
		mindbox?.displayProductDetail({ product: { uuid } });
	}, [modelId]);

	const handleClickOpenShops = useCallback(() => {
		dispatch(fetchProductShops({ slug: modelSlug }));
	}, [dispatch, modelSlug]);

	return (
		<>
			<MetaData
				title={title}
				description={description}
				seo={seo}
				ogTitle={ogTitle}
				ogDescription={ogDescription}
				image={ogImageRect || data?.gallery?.[0]?.x2}
				imageSmall={ogImageSquare || data?.gallery?.[0]?.x2}
				type='product'
				productData={data}
			/>
			<JsonLdData product={data} />

			<Container>
				<section className={styles.section}>
					<Product
						data={{
							...data,
							clickOpenShops: handleClickOpenShops,
							shops: shopsData?.shops,
							loadingShops,
						}}
						productInfo={productInfoGA}
					/>
				</section>
			</Container>
			<div>
				<Collections
					key={modelId}
					collectionIds={data.collection_ids}
					lookId={data.look_id}
					modelSlug={modelSlug}
					currency={currency}
				/>
			</div>
			{historyProducts && historyProducts.length > 0 && (
				<Container>
					<Viewed
						products={historyProducts}
						key={modelId}
						currency={currency}
						setFirstProductId={setFirstProductId}
						firstProductId={firstProductId}
					/>
				</Container>
			)}
			<Container>
				<div className={styles.breadCrumbs}>
					{breadcrumbs_second && breadcrumbs_second.length > 0 && (
						<BreadCrumbs breadcrumbs={breadcrumbs_second} />
					)}
				</div>
			</Container>
		</>
	);
};

export default ProductPage;
